import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../constants/queryKeys";
import { datadogLogs } from "@datadog/browser-logs";
import {
  downloadGeneratedMP3Track,
  downloadTrack,
} from "../../store/actions/audioService";
import { useAppDispatch } from "../../store/hooks";

export interface DownloadTrackParams {
  fileVersionId?: number;
  code?: string;
  projectId?: number;
  isSnippet?: boolean;
  spotifyPreviewUrl?: string;
  use_generated_mp3?: boolean;
  enabled?: boolean;
  onSuccess?: () => void;
  extraAction?: () => void;
}

interface GenerateQueryKeyForFetchTrackParams extends DownloadTrackParams {
  isMusoTrack?: boolean;
}

export const generateQueryKeyForFetchTrack = ({
  fileVersionId,
  code,
  projectId,
  isSnippet,
  use_generated_mp3,
  spotifyPreviewUrl,
}: GenerateQueryKeyForFetchTrackParams) => {
  const queryKey = [];

  if (spotifyPreviewUrl) {
    queryKey.push(QUERY_KEYS.FETCH_MUSO_TRACK);
    queryKey.push(spotifyPreviewUrl);
    return queryKey;
  }

  queryKey.push(QUERY_KEYS.FETCH_TRACK);
  if (fileVersionId) {
    queryKey.push(fileVersionId);
  }
  if (code) {
    queryKey.push(code);
  }
  if (projectId) {
    queryKey.push(projectId);
  }
  if (isSnippet) {
    queryKey.push(isSnippet);
  }
  if (use_generated_mp3) {
    queryKey.push(use_generated_mp3);
  }
  return queryKey;
};

export const useFetchTrackMp3 = ({
  fileVersionId,
  code,
  projectId,
  isSnippet,
  spotifyPreviewUrl,
  enabled = true,
  use_generated_mp3 = true,
  onSuccess = () => {},
  extraAction,
}: DownloadTrackParams) => {
  const dispatch = useAppDispatch();
  const queryKey = generateQueryKeyForFetchTrack({
    fileVersionId,
    code,
    projectId,
    isSnippet,
    use_generated_mp3,
  });
  const query = useQuery({
    enabled: enabled,
    queryKey,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: false,
    retry: 0,
    refetchOnMount: true,
    queryFn: async () => {
      if (spotifyPreviewUrl) {
        onSuccess();
        return {
          url: spotifyPreviewUrl,
          content_type: "audio/mp3",
        };
      }
      try {
        const { url, content_type, waveform_peaks, duration } = await dispatch(
          downloadGeneratedMP3Track({
            fileVersionId,
            code,
            projectId,
            isSnippet,
          }),
        ).unwrap();
        onSuccess();
        return {
          url,
          content_type,
          waveform_peaks,
          duration,
        };
      } catch (e) {
        datadogLogs.logger.error("Error downloading mp3", {
          fileVersionId,
          code,
          projectId,
          isSnippet,
          use_generated_mp3,
        });
      } finally {
        extraAction?.();
      }
    },
  });
  return { query, queryKey };
};

export const useFetchTrack = ({
  fileVersionId,
  code,
  projectId,
  isSnippet,
  use_generated_mp3 = false,
  extraAction,
}: DownloadTrackParams) => {
  const dispatch = useAppDispatch();
  const queryKey = generateQueryKeyForFetchTrack({
    fileVersionId,
    code,
    projectId,
    isSnippet,
    use_generated_mp3,
  });

  const query = useQuery({
    enabled: Boolean(fileVersionId || code),
    queryKey,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchInterval: false,
    retry: 0,
    refetchOnMount: true,
    queryFn: async () => {
      try {
        const { url, content_type, waveform_peaks, duration } = await dispatch(
          downloadTrack({
            fileVersionId,
            code,
            projectId,
          }),
        ).unwrap();
        extraAction?.();

        return {
          url,
          content_type,
          waveform_peaks,
          duration,
        };
      } catch (e) {
        extraAction?.();
        datadogLogs.logger.error("Error downloading track", {
          fileVersionId,
          code,
          projectId,
          isSnippet,
          use_generated_mp3,
        });
      } finally {
        extraAction?.();
      }
    },
  });
  return { query, queryKey };
};
